import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlaskConical, X, AlertTriangle, Sparkles } from 'lucide-react';
import ProcedureRunner from '@/components/ProcedureRunner';

/**
 * Pop-up shown when a chat/guided-diagnosis response contains one or more recommended
 * Universal Procedures. It surfaces the suggestion prominently so the technician can
 * jump straight into the runner without scrolling through the response.
 *
 * Behaviour:
 *  - Auto-opens once per `signature` (signature is typically the joined procedure ids
 *    of the new response). When the user clicks "Più tardi" or closes the modal, the
 *    signature is remembered so the popup does not re-open for the same response.
 *  - Always offers "Procedi con questa procedura" buttons that delegate to ProcedureRunner.
 *  - Designed to be dropped into pages that already have universal_procedures in state.
 *
 * Props:
 *   procedures: array of universal procedures (id, category, title, safety_warnings, steps, ...)
 *   context:    { brand, model, machineId?, question }
 *   lang:       'it'|'es'|'en'
 *   onRunnerCompleted?: () => void  -- forwarded to ProcedureRunner.onCompleted
 */
export default function RecommendedTestPopup({ procedures = [], context = {}, lang = 'it', onRunnerCompleted }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState('');

  // Compute a stable signature: change when the AI returns a different set of suggestions
  const signature = (procedures || []).map((p) => p.id).join('|');

  useEffect(() => {
    if (!signature) { setOpen(false); return; }
    if (signature === dismissed) return; // user already saw / dismissed this one
    setOpen(true);
  }, [signature, dismissed]);

  if (!procedures || procedures.length === 0) return null;
  if (!open) return null;

  // Backdrop click: temporarily close the popup without marking the signature as
  // dismissed, so the user gets a chance to reopen it (re-mount). 'X' and 'Più tardi'
  // both persist the dismissal so the popup stays closed for this AI response.
  const closeTemp = () => setOpen(false);
  const dismiss = () => {
    setDismissed(signature);
    setOpen(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 animate-fade-up"
      data-testid="recommended-test-popup"
      onClick={closeTemp}
    >
      <div
        className="bg-white border-2 border-indigo-600 max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 bg-gradient-to-r from-indigo-700 to-indigo-600 text-white flex items-start gap-3">
          <div className="relative">
            <FlaskConical className="w-7 h-7 shrink-0" />
            <Sparkles className="w-3 h-3 text-yellow-300 absolute -top-1 -right-1 animate-pulse" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="overline text-indigo-200 text-[10px]">{t('recommended.tag') || 'TEST CONSIGLIATO'}</div>
            <h2 className="font-display font-bold text-lg leading-tight">
              {procedures.length === 1
                ? (t('recommended.title_singular') || 'Test universale consigliato')
                : (t('recommended.title_plural', { n: procedures.length }) || `${procedures.length} test universali consigliati`)}
            </h2>
            <p className="text-indigo-100 text-xs mt-1">
              {t('recommended.subtitle') || 'Basato sulla diagnosi corrente. Procedi per registrare le misure e salvare automaticamente nell\'archivio interventi.'}
            </p>
          </div>
          <button
            onClick={dismiss}
            className="text-white/80 hover:text-white shrink-0"
            data-testid="recommended-popup-close"
            aria-label="close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {procedures.map((p) => {
            const title = (p.title || {})[lang] || (p.title || {}).it || (p.title || {}).en || p.category;
            const safety = (p.safety_warnings || {})[lang] || '';
            const steps = p.steps || [];
            return (
              <div
                key={p.id}
                className="border border-indigo-200 bg-indigo-50/30 p-4"
                data-testid={`recommended-proc-${p.id}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="bg-indigo-100 text-indigo-900 px-2 py-0.5 text-[10px] font-mono uppercase">
                        {p.category}
                      </span>
                      <span className="font-mono text-[10px] text-gray-500">{steps.length} step</span>
                    </div>
                    <h3 className="font-display font-bold text-base">{title}</h3>
                  </div>
                  <ProcedureRunner
                    procedure={p}
                    lang={lang}
                    context={context}
                    onCompleted={(execution) => {
                      // After the runner saves the execution, persist dismissal so the
                      // popup does NOT re-open for the same AI response.
                      dismiss();
                      if (onRunnerCompleted) onRunnerCompleted(execution);
                    }}
                  />
                </div>
                {safety && (
                  <div className="mt-3 border-l-4 border-red-500 bg-red-50 p-2 text-xs flex items-start gap-2" data-testid={`recommended-safety-${p.id}`}>
                    <AlertTriangle className="w-3.5 h-3.5 text-red-600 shrink-0 mt-0.5" />
                    <span className="text-red-900 whitespace-pre-wrap">{safety}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-2">
          <button
            onClick={dismiss}
            className="btn-secondary text-sm"
            data-testid="recommended-popup-later"
          >
            {t('recommended.later') || 'Più tardi'}
          </button>
        </div>
      </div>
    </div>
  );
}
